import {
  playerUiStates,
  changeState,
  setPlayerUiHideTimeout,
  destroyPauseScreenTimer,
  playbackStates,
  resetPauseScreenTimer
} from "./playerStates";
import { getSdk } from "../../services/sdkService";
import getRemoteKey from "../../services/remoteService";
import { config } from "../../config/config";

let conf;

let sdk;
(async function() {
  sdk = await getSdk();
  conf = await config.get();
})(); // Autocalled function

export const keyActions = {
  OK: "OK",
  BACK: "BACK",
  ARROW_UP: "ARROW_UP",
  ARROW_RIGHT: "ARROW_RIGHT",
  ARROW_DOWN: "ARROW_DOWN",
  ARROW_LEFT: "ARROW_LEFT",
  ANY: "ANY",
  MEDIA_FAST_FORWARD: "MEDIA_FAST_FORWARD",
  MEDIA_REWIND: "MEDIA_REWIND",
  MEDIA_PLAY: "MEDIA_PLAY",
  MEDIA_PAUSE: "MEDIA_PAUSE",
  MEDIA_STOP: "MEDIA_STOP"
};

const fwd = "forward";
const rwd = "rewind";

let mediaKeyActive = null;

/**
 * The list of keys to which the player responds on the state CONTROLS states.
 * This object contains a list of properties corresponding to the keys that cause behavior in the CONTROLS state, which point
 * to the function that will implement that behavior. The keys follow a common key map that will have different values
 * per flavor (check remoteService).
 */
const controlsKeyHandling = {
  ARROW_RIGHT: function() {
    changeState.call(this, playerUiStates.FILMSTRIP);
  },
  ARROW_LEFT: function() {
    changeState.call(this, playerUiStates.FILMSTRIP);
  },
  MEDIA_FAST_FORWARD: function() {
    this.state.activePanel !== playerUiStates.MEDIA_KEYS &&
      this.state.activePanel !== playerUiStates.FILMSTRIP &&
      setMediaKeysState.call(this, keyActions.MEDIA_FAST_FORWARD);
    ffInterval.call(this);
  },
  MEDIA_REWIND: function() {
    this.state.activePanel !== playerUiStates.MEDIA_KEYS &&
      this.state.activePanel !== playerUiStates.FILMSTRIP &&
      setMediaKeysState.call(this, keyActions.MEDIA_REWIND);
    rwInterval.call(this);
  },
  MEDIA_STOP: function() {
    mediaStop.call(this);
  }
};

/**
 * The list of keys to which the player responds on the NONE and SKIP_INTRO states.
 */
const controlKeyHandlersForNoneState = {
  ARROW_UP: function() {
    changeState.call(
      this,
      this.state.playbackState === playbackStates.PAUSE
        ? playerUiStates.CONTROLS_PAUSED
        : playerUiStates.CONTROLS_PLAYING
    );
  },
  ARROW_DOWN: function() {
    changeState.call(
      this,
      this.state.playbackState === playbackStates.PAUSE
        ? playerUiStates.CONTROLS_PAUSED
        : playerUiStates.CONTROLS_PLAYING
    );
  },
  ARROW_RIGHT: function() {
    changeState.call(this, playerUiStates.FILMSTRIP, setMediaKeyActive.bind(this, keyActions.MEDIA_FAST_FORWARD));
  },
  ARROW_LEFT: function() {
    changeState.call(this, playerUiStates.FILMSTRIP, setMediaKeyActive.bind(this, keyActions.MEDIA_REWIND));
  },
  MEDIA_FAST_FORWARD: function() {
    setMediaKeysState.call(this, keyActions.MEDIA_FAST_FORWARD);
    ffInterval.call(this);
  },
  MEDIA_REWIND: function() {
    setMediaKeysState.call(this, keyActions.MEDIA_REWIND);
    rwInterval.call(this);
  },
  MEDIA_PAUSE: function() {
    changeState.call(this, playerUiStates.PAUSE);
  },
  MEDIA_STOP: function() {
    mediaStop.call(this);
  },
  OK: function() {
    if (this.state.playbackState === playbackStates.PAUSE) {
      sdk.playerManager.play();
      changeState.call(this, playerUiStates.CONTROLS_PLAYING);
    } else {
      changeState.call(this, playerUiStates.PAUSE);
    }
  }
};

/**
 * The list of keys to which the player responds on each state. This object contains a property per player state and
 * each of them has a list of keys with a function that will be called when there is a key press in the player while
 * the active state is the corresponding one. The keys follow a common key map that will have different values per
 * flavor (check remoteService)
 */
export const keyHandlers = {
  none: {
    ...controlKeyHandlersForNoneState
  },
  skipIntro: {
    ...controlKeyHandlersForNoneState
  },
  controlsPlaying: {
    ...controlsKeyHandling,
    MEDIA_PAUSE: function() {
      changeState.call(this, playerUiStates.PAUSE);
    },
    ARROW_DOWN: function() {
      changeState.call(this, playerUiStates.NONE);
    },
    ARROW_RIGHT: function() {
      changeState.call(this, playerUiStates.FILMSTRIP, setMediaKeyActive.bind(this, keyActions.MEDIA_FAST_FORWARD));
    },
    ARROW_LEFT: function() {
      changeState.call(this, playerUiStates.FILMSTRIP, setMediaKeyActive.bind(this, keyActions.MEDIA_REWIND));
    },
    MEDIA_FAST_FORWARD: function() {
      setMediaKeysState.call(this, keyActions.MEDIA_FAST_FORWARD);
      ffInterval.call(this);
    },
    MEDIA_REWIND: function() {
      setMediaKeysState.call(this, keyActions.MEDIA_REWIND);
      rwInterval.call(this);
    }
  },
  controlsPaused: {
    ...controlsKeyHandling,
    MEDIA_PLAY: function() {
      destroyPauseScreenTimer.call(this);
      changeState.call(this, playerUiStates.CONTROLS_PLAYING);
    }
  },
  tracks: {
    BACK: function() {
      changeState.call(this, playerUiStates.CONTROLS_PLAYING);
    },
    MEDIA_STOP: function() {
      mediaStop.call(this);
    }
  },
  episodeSelector: {
    BACK: function() {
      changeState.call(this, playerUiStates.CONTROLS_PAUSED);
    },
    MEDIA_STOP: function() {
      mediaStop.call(this);
    }
  },
  filmstrip: {
    ARROW_RIGHT: function() {
      resetFastForward.call(this);
      resetRewind.call(this);
      resetPlaybackRate.call(this);
      if (this.filmstrip) {
        !this.filmstrip.isLastSlide && filmstripToRight.call(this);
      } else {
        const currentTime = getCurrentTimeFForward.call(this);
        if (currentTime < this.state.duration) {
          setMediaKeyActive.call(this, keyActions.MEDIA_FAST_FORWARD);
          fastForward.call(this);
        }
      }
    },
    ARROW_LEFT: function() {
      resetFastForward.call(this);
      resetRewind.call(this);
      resetPlaybackRate.call(this);
      if (this.filmstrip) {
        !this.filmstrip.isFirstSlide && filmstripToLeft.call(this);
      } else {
        const currentTime = getCurrentTimeRewind.call(this);
        if (currentTime > 0) {
          setMediaKeyActive.call(this, keyActions.MEDIA_REWIND);
          rewind.call(this);
        }
      }
    },
    MEDIA_FAST_FORWARD: function() {
      !mediaKeyActive && setMediaKeyActive.call(this, keyActions.MEDIA_FAST_FORWARD);
      ffInterval.call(this);
    },
    MEDIA_REWIND: function() {
      !mediaKeyActive && setMediaKeyActive.call(this, keyActions.MEDIA_REWIND);
      rwInterval.call(this);
    },
    ARROW_DOWN: function() {
      changeState.call(this, playerUiStates.CONTROLS_PLAYING);
    },
    OK: function() {
      mediaKeysContinue.call(this);
    },
    MEDIA_PLAY: function() {
      mediaKeysContinue.call(this);
    },
    MEDIA_STOP: function() {
      mediaStop.call(this);
    }
  },
  pause: {
    OK: function() {
      destroyPauseScreenTimer.call(this);
      changeState.call(this, playerUiStates.CONTROLS_PLAYING);
    },
    ARROW_RIGHT: function() {
      resetPauseScreenTimer.call(this);
      changeState.call(this, playerUiStates.CONTROLS_PAUSED);
    },
    ARROW_LEFT: function() {
      resetPauseScreenTimer.call(this);
      changeState.call(this, playerUiStates.CONTROLS_PAUSED);
    },
    ARROW_UP: function() {
      resetPauseScreenTimer.call(this);
      changeState.call(this, playerUiStates.CONTROLS_PAUSED);
    },
    ARROW_DOWN: function() {
      resetPauseScreenTimer.call(this);
      changeState.call(this, playerUiStates.CONTROLS_PAUSED);
    },
    MEDIA_PLAY: function() {
      destroyPauseScreenTimer.call(this);
      changeState.call(this, playerUiStates.CONTROLS_PLAYING);
    },
    MEDIA_FAST_FORWARD: function() {
      setMediaKeysState.call(this, keyActions.MEDIA_FAST_FORWARD);
      ffInterval.call(this);
    },
    MEDIA_REWIND: function() {
      setMediaKeysState.call(this, keyActions.MEDIA_REWIND);
      rwInterval.call(this);
    },
    BACK: function() {
      resetPauseScreenTimer.call(this);
      changeState.call(this, playerUiStates.CONTROLS_PAUSED);
    },
    MEDIA_STOP: function() {
      mediaStop.call(this);
    }
  },
  mediaKeys: {
    OK: function() {
      mediaKeysContinue.call(this);
    },
    MEDIA_PLAY: function() {
      mediaKeysContinue.call(this);
    },
    MEDIA_FAST_FORWARD: function() {
      ffInterval.call(this);
    },
    MEDIA_REWIND: function() {
      rwInterval.call(this);
    },
    BACK: function() {
      changeState.call(this, playerUiStates.CONTROLS_PLAYING);
    },
    MEDIA_STOP: function() {
      mediaStop.call(this);
    }
  },
  nextEpisode: {
    BACK: function() {
      changeState.call(this, playerUiStates.NONE);
    },
    MEDIA_STOP: function() {
      mediaStop.call(this);
    }
  }
};

/**
 * Called when press stop key on MEDIA_KEYS state
 */
function mediaStop() {
  resetFastForward.call(this);
  resetRewind.call(this);
  this.backHandler();
}

/**
 * Sets state depends on media key pressed (fwd, rwd)
 * @param {String} mediaKey
 */
function setMediaKeysState(mediaKey) {
  setMediaKeyActive.call(this, mediaKey);
  if (this.state.filmstripData) {
    changeState.call(this, playerUiStates.FILMSTRIP);
  } else {
    changeState.call(this, playerUiStates.MEDIA_KEYS);
  }
}

function getTimestampToGo(filmstripPosition) {
  return (
    this.state.filmstripData.thumbnails[filmstripPosition] &&
    this.state.filmstripData.thumbnails[filmstripPosition].timestamp
  );
}

/**
 * Moves filmstrip to right
 */
function filmstripToRight() {
  const newFilmstripPosition = this.filmstrip.currentSlide + 1;
  let timestampToGo = getTimestampToGo.call(this, newFilmstripPosition);
  if (!timestampToGo) {
    resetFastForward.call(this);
    return false;
  }
  updateFilmstripState.call(this, timestampToGo, newFilmstripPosition);
  this.filmstrip.goTo(newFilmstripPosition);
}

/**
 * Moves filmstrip to left
 */
function filmstripToLeft() {
  const newFilmstripPosition = this.filmstrip.currentSlide - 1;
  let timestampToGo = getTimestampToGo.call(this, newFilmstripPosition);
  if (!timestampToGo || timestampToGo < 0) {
    timestampToGo = 1;
  }
  updateFilmstripState.call(this, timestampToGo, newFilmstripPosition);
  timestampToGo >= 0 && !this.filmstrip.isFirstSlide && this.filmstrip.goTo(newFilmstripPosition);
  if (timestampToGo === 0) {
    resetRewind.call(this);
  }
}

/**
 * Updates state for filmstrip carousel
 * @param {Number} tentativePosition
 * @param {Number} currentFilmstripPosition
 */
function updateFilmstripState(tentativePosition, currentFilmstripPosition) {
  this.setState({
    tentativePosition,
    currentFilmstripPosition
  });
}

/**
 * Resets playback rate (fwd and rwd) and component state
 */
function resetPlaybackRate() {
  this.setState(() => ({
    playbackRate: 0
  }));
}

/**
 * Updates playback rate (fwd and rwd) and component state
 */
function updatePlaybackRate() {
  if (this.state.playbackRate === 3) {
    resetPlaybackRate.call(this);
  }
  this.setState(prevState => ({
    playbackRate: prevState.playbackRate + 1
  }));
}

/**
 * Returns playback rate
 * @returns {Number}
 */
async function getPlaybackRate() {
  await updatePlaybackRate.call(this);
  return conf.player.playbackRates[this.state.playbackRate];
}

/**
 * Called when OK or PLAY buttons are pressed in MEDIA_KEYS or FILMSTRIP player state.
 * Resets fwd and rwd timers and active keys, set new player state and goes to tentative position
 */
function mediaKeysContinue() {
  const position = this.state.tentativePosition !== null ? this.state.tentativePosition : this.state.currentTime;
  resetFastForward.call(this);
  changeState.call(this, playerUiStates.NONE);
  resetRewind.call(this);
  (!!position || position === 0) &&
    sdk.playerManager.seek(position, () => {
      setMediaKeyActive.call(this, null);
    });
}

/**
 * Clear fwd interval
 */
function clearFFInterval() {
  this.ffInterval && clearInterval(this.ffInterval);
  this.ffInterval = null;
}

/**
 * Resets fwd interval and goes to tentative position
 */
function resetFastForward() {
  if (this.ffInterval) {
    clearFFInterval.call(this);
    resetPlaybackRate.call(this);
  }
}

/**
 * Clear rwd interval
 */
function clearRWInterval() {
  this.rwInterval && clearInterval(this.rwInterval);
  this.rwInterval = null;
}

/**
 * Resets rwd interval and goes to tentative position
 */
function resetRewind() {
  if (this.rwInterval) {
    clearRWInterval.call(this);
    resetPlaybackRate.call(this);
  }
}

function getCurrentTimeFForward() {
  return this.state.currentTime + conf.sdk.filmstripsTimeJump;
}

function getCurrentTimeRewind() {
  return this.state.currentTime - conf.sdk.filmstripsTimeJump;
}

/**
 * Sets interval for fwd
 */
async function ffInterval() {
  if (mediaKeyActive === keyActions.MEDIA_REWIND) {
    resetPlaybackRate.call(this);
    clearRWInterval.call(this);
    setMediaKeyActive.call(this, keyActions.MEDIA_FAST_FORWARD);
  }
  clearFFInterval.call(this);
  this.ffInterval = setInterval(fastForward.bind(this), await getPlaybackRate.call(this));
}

/**
 * Sets interval for rwd
 */
async function rwInterval() {
  if (mediaKeyActive === keyActions.MEDIA_FAST_FORWARD) {
    resetPlaybackRate.call(this);
    clearFFInterval.call(this);
    setMediaKeyActive.call(this, keyActions.MEDIA_REWIND);
  }
  clearRWInterval.call(this);
  this.rwInterval = setInterval(rewind.bind(this), await getPlaybackRate.call(this));
}

/**
 * Forward moving at 10 secs steps
 */
function fastForwardMediaKeys() {
  const currentTime = getCurrentTimeFForward.call(this);
  if (currentTime >= this.state.duration) {
    resetFastForward.call(this);
    changeState.call(this, playerUiStates.CONTROLS_PLAYING);
    return false;
  }
  this.setState({
    tentativePosition: currentTime,
    currentTime: currentTime
  });
}

/**
 * Checks if the fwd is from media or right key
 */
function fastForward() {
  const currentTime = getCurrentTimeFForward.call(this);
  if (
    (this.state.activePanel === playerUiStates.MEDIA_KEYS ||
      (this.state.activePanel === playerUiStates.FILMSTRIP && !this.filmstrip)) &&
    currentTime < this.state.duration
  ) {
    fastForwardMediaKeys.call(this);
  } else if (this.state.activePanel === playerUiStates.FILMSTRIP) {
    filmstripToRight.call(this);
  }
}

/**
 * Rewind moving at 10 secs steps
 */
function rewindMediaKeys() {
  const currentTime = getCurrentTimeRewind.call(this);
  if (currentTime <= 0) {
    resetRewind.call(this);
    changeState.call(this, playerUiStates.CONTROLS_PLAYING);
    return false;
  }
  this.setState({
    tentativePosition: currentTime,
    currentTime: currentTime
  });
}

/**
 * Checks if the rwd is from media or left key
 */
function rewind() {
  const currentTime = getCurrentTimeRewind.call(this);
  if (
    (this.state.activePanel === playerUiStates.MEDIA_KEYS ||
      (this.state.activePanel === playerUiStates.FILMSTRIP && !this.filmstrip)) &&
    currentTime > 0
  ) {
    rewindMediaKeys.call(this);
  } else if (this.state.activePanel === playerUiStates.FILMSTRIP) {
    filmstripToLeft.call(this);
  }
}

/**
 * Callback called when any media key is pressed
 * @param {String} pressedKey
 */
function onMediaKeyAction(pressedKey) {
  keyHandlers[this.state.activePanel] &&
    keyHandlers[this.state.activePanel][pressedKey] &&
    keyHandlers[this.state.activePanel][pressedKey].call(this);
}

/**
 * Returns if any media key has pressed
 * @param {String} pressedKey
 * @returns {Boolean}
 */
function isMediaKeyPressed(pressedKey) {
  return (
    pressedKey === keyActions.MEDIA_FAST_FORWARD ||
    pressedKey === keyActions.MEDIA_REWIND ||
    pressedKey === keyActions.MEDIA_PLAY ||
    pressedKey === keyActions.MEDIA_PAUSE ||
    pressedKey === keyActions.MEDIA_STOP
  );
}

/**
 * Sets wich media key (fwd or rwd) has pressed
 * @param {String} mediaKey
 */
function setMediaKeyActive(mediaKey) {
  mediaKeyActive = mediaKey;
  if (mediaKeyActive === keyActions.MEDIA_FAST_FORWARD) {
    this.playbackIconName = fwd;
  } else if (mediaKeyActive === keyActions.MEDIA_REWIND) {
    this.playbackIconName = rwd;
  } else {
    this.playbackIconName = null;
  }
}

/**
 * Resets all timers, state, active media keys and rates
 */
export function resetTimers() {
  clearFFInterval.call(this);
  clearRWInterval.call(this);
  resetPlaybackRate.call(this);
  setMediaKeyActive.call(this, null);
  this.setState({
    currentFilmstripPosition: null,
    tentativePosition: null
  });
}

/**
 * Function to detect if there is a keypress on any screen except the controlsPaused in playing state
 *
 * @returns {Boolean} True in case of keypress is on the controls_paused when playback is not pause false if not
 */
function keyPressTimer() {
  const { activePanel, playbackState } = this.state;
  return activePanel !== playerUiStates.CONTROLS_PAUSED && playbackState !== playerUiStates.PAUSE;
}

/**
 * Function to detect if there is a keypress on pause/none screen in case the player in pause state
 *
 * @returns {Boolean} True in case of keypress is on the controls_paused and playback state is pause false if not
 */
function keyPausedPressTimer() {
  const { activePanel, playbackState } = this.state;
  return (
    [playerUiStates.CONTROLS_PAUSED, playerUiStates.NONE].indexOf(activePanel) > -1 &&
    playbackState === playerUiStates.PAUSE
  );
}

/**
 * Callback wich is called each time that any key is pressed
 * @param {Object} event
 */
export function onAnyKeyPressed(event) {
  const activePanel = this.state.activePanel;
  const pressedKey = getRemoteKey(event.keyCode);
  this.mounted && isMediaKeyPressed(pressedKey) && onMediaKeyAction.call(this, pressedKey);
  if (
    (this.state.playerBuffering && pressedKey !== keyActions.BACK) ||
    pressedKey === keyActions.MEDIA_STOP ||
    this.state.episodeSelectorLoading
  ) {
    return;
  }
  const notMediaRewindOrFForward = [keyActions.MEDIA_FAST_FORWARD, keyActions.MEDIA_REWIND].indexOf(pressedKey) < 0;
  const isNotPanelException =
    [playerUiStates.FILMSTRIP, playerUiStates.PAUSE, playerUiStates.CONTROLS_PAUSED].indexOf(activePanel) < 0;
  const isNotTrackOkpress = activePanel === playerUiStates.TRACKS && pressedKey === keyActions.OK;
  const isNavigating =
    [keyActions.ARROW_LEFT, keyActions.ARROW_RIGHT, keyActions.ARROW_DOWN, keyActions.ARROW_UP].indexOf(pressedKey) !==
    -1;

  if (
    ((isNotPanelException && notMediaRewindOrFForward) || isNotTrackOkpress || isNavigating) &&
    activePanel !== playerUiStates.NEXT_EPISODE
  ) {
    setPlayerUiHideTimeout.call(this);
  }

  if (keyPressTimer.call(this)) {
    destroyPauseScreenTimer.call(this);
  } else if (keyPausedPressTimer.call(this)) {
    resetPauseScreenTimer.call(this);
  }
}
